import {
    useOrthopulseOrderPreview,
    usePreviewOrthopulseOrderWithShipTo,
    useSendOrthopulseOrder
} from '../../../../../hooks/useOrthopulseOrder';
import { useMyPermissions } from '../../../../../hooks/useMyPermissions';
import { validateShipTo, type ShipToFormErrors } from './shipToValidation';
import { useShipToForms } from './useShipToForms';
import { useForceOverrideState, type UseForceOverrideStateResult } from './useForceOverrideState';
import { useOrthopulseOrderActions } from './useOrthopulseOrderActions';
import { useOrthopulseStep } from './useOrthopulseStep';
import type { OrthopulseOrderStep } from './step';

export interface UseOrthopulseOrderFlowResult {
    initialPreview: ReturnType<typeof useOrthopulseOrderPreview>;
    previewWithShipTo: ReturnType<typeof usePreviewOrthopulseOrderWithShipTo>;
    sendOrder: ReturnType<typeof useSendOrthopulseOrder>;
    step: OrthopulseOrderStep;
    goToShipTo: () => void;
    shipToForms: ReturnType<typeof useShipToForms>;
    errors: ShipToFormErrors;
    isPreviewDisabled: boolean;
    forceOverride: UseForceOverrideStateResult;
    cancelForceOverride: () => void;
    isForcing: boolean;
    handlePreview: () => void;
    handleSend: () => void;
}

// Composition root for the Order OrthoPulse flow - wires the queries,
// ship-to forms, and force-override/step state together for rendering.
export function useOrthopulseOrderFlow(
    patientId: string,
    onDataChange?: () => void
): UseOrthopulseOrderFlowResult {
    const { hasPermission } = useMyPermissions();
    const canForceOrder = hasPermission('billing.update');

    const initialPreview = useOrthopulseOrderPreview(patientId);
    const previewWithShipTo = usePreviewOrthopulseOrderWithShipTo(patientId);
    const sendOrder = useSendOrthopulseOrder(patientId);

    const shipToForms = useShipToForms(initialPreview.data?.shipToDefaults);
    const errors = validateShipTo(shipToForms.values);
    const isValid = Object.keys(errors).length === 0;

    const blocked = initialPreview.data?.blocked ?? null;
    const forceOverride = useForceOverrideState(canForceOrder, blocked);
    const { step, setStep, goToShipTo, cancelForceOverride } = useOrthopulseStep(forceOverride);

    const { handlePreview, handleSend, isPreviewDisabled } = useOrthopulseOrderActions({
        shipToForms,
        isValid,
        forceOverride,
        previewWithShipTo,
        sendOrder,
        setStep,
        onDataChange
    });

    return {
        initialPreview,
        previewWithShipTo,
        sendOrder,
        step,
        goToShipTo,
        shipToForms,
        errors,
        isPreviewDisabled,
        forceOverride,
        cancelForceOverride,
        isForcing: forceOverride.isReady,
        handlePreview,
        handleSend
    };
}
